<template>
  <div>
    <a-table
      :columns="computedColumns"
      :data-source="localStudents"
      :loading="loading"
      :pagination="false"
      row-key="enrollment_id"
      size="small"
      bordered
      :scroll="{ x: 'max-content' }"
    >
      <!-- Dynamic Subject Columns -->
      <template v-for="col in subjectColumns" :slot="col.scopedSlots.customRender" slot-scope="text, record">
        <span :key="col.key">
          {{ getStudentSubjectMark(record.marks, col.subject, col.type) }}
        </span>
      </template>

      <!-- Totals & Rank (Read-Only) -->
      <template slot="total" slot-scope="text, record">
        {{ record.total_mark || (record.total_mark === 0 ? 0 : '--') }}
      </template>
      <template slot="rank" slot-scope="text, record">
        {{ record.class_rank || '--' }}
      </template>

      <!-- Editable Remark -->
      <template slot="exam_remark" slot-scope="text, record">
        <a-input
          v-model="record.exam_remark"
          placeholder="Enter Remark"
          style="width: 100%"
        />
      </template>
    </a-table>

    <div class="mt-4 text-right">
      <a-button type="primary" :loading="saving" @click="saveRemarks" icon="save">
        Save Remarks
      </a-button>
    </div>
  </div>
</template>

<script>

export default {
  name: 'RemarkEntryTable',
  props: {
    students: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    examData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      saving: false,
      localStudents: [],
    }
  },
  computed: {
    subjectColumns() {
      if (!this.examData || !this.examData.class_section_exam_subject) {
        return []
      }
      const columns = []
      this.examData.class_section_exam_subject.forEach((sub) => {
        if (sub.has_practical) {
          columns.push({
            title: `${sub.subject.abbreviation}-T`,
            key: `subject-${sub.id}-theory`,
            scopedSlots: { customRender: `subject-${sub.id}-theory` },
            subject: sub,
            type: 'theory',
            width: 80,
          })
          columns.push({
            title: `${sub.subject.abbreviation}-P`,
            key: `subject-${sub.id}-practical`,
            scopedSlots: { customRender: `subject-${sub.id}-practical` },
            subject: sub,
            type: 'practical',
            width: 80,
          })
        } else {
          columns.push({
            title: sub.subject.abbreviation,
            key: `subject-${sub.id}-total`,
            scopedSlots: { customRender: `subject-${sub.id}-total` },
            subject: sub,
            type: 'total',
            width: 80,
          })
        }
      })
      return columns
    },
    computedColumns() {
      const baseColumns = [
        { title: 'Roll', dataIndex: 'roll_no', key: 'roll_no', width: 60, fixed: 'left' },
        { title: 'S. Code', dataIndex: 'student_code', key: 'student_code', width: 80, fixed: 'left' },
        { title: 'Name', dataIndex: 'student_name', key: 'student_name', width: 200, fixed: 'left' },
      ]

      const endColumns = [
        { title: 'Total', key: 'total', scopedSlots: { customRender: 'total' }, width: 80 },
        { title: 'Result', dataIndex: 'result', key: 'result', width: 80 },
        { title: 'Rank', key: 'rank', scopedSlots: { customRender: 'rank' }, width: 80 },
        { title: 'Remark', key: 'exam_remark', scopedSlots: { customRender: 'exam_remark' }, width: 300 },
      ]

      return [...baseColumns, ...this.subjectColumns, ...endColumns]
    },
  },
  watch: {
    students: {
      immediate: true,
      handler(newList) {
        this.localStudents = newList.map((s) => ({ ...s }))
      },
    },
  },
  methods: {
    getStudentSubjectMark(marks, examSubject, type) {
      if (!marks) return '--'
      const studentMark = marks.find(m => m.subject_abbr === examSubject.id)
      if (!studentMark) return '--'

      const value = examSubject.scholastic
        ? (studentMark.mark !== null ? studentMark.mark : (studentMark.grade || '--'))
        : (studentMark.grade || (studentMark.mark !== null ? studentMark.mark : '--'))
      return value
    },
    saveRemarks() {
      this.saving = true

      const studentRemarks = this.localStudents
        .filter(s => s.exam_remark && String(s.exam_remark).trim() !== '')
        .map(s => ({
          enrollment_id: s.enrollment_id,
          exam_remark: s.exam_remark,
        }))

      const payload = {
        class_section_exam_id: this.examData.id,
        student_remarks: studentRemarks,
      }

      this.$http
        .post('/api/studentexamremark/save', payload)
        .then(() => {
          this.$notification.success({
            message: 'Success',
            description: 'Remarks successfully saved',
          })
          this.$emit('refresh')
        })
        .catch((error) => {
          this.$neoNotify.error(error)
        })
        .finally(() => {
          this.saving = false
        })
    },
  },
}
</script>
